import React, { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import {
  Bot,
  ChevronDown,
  Globe,
  Loader2,
  MessageSquare,
  Search,
  User,
} from "lucide-react";
import Sidebar from "@/components/Sidebar";
import { getClientAuthToken } from "@/lib/apiAuth";
import { WEBSITE_LEAD_INTERCEPTOR_COPY } from "@/constants/websiteLeadInterceptor";

const BACKEND_URL = import.meta.env.VITE_BACKEND_URL || process.env.REACT_APP_BACKEND_URL;

const formatDate = (timestamp) => {
  if (!timestamp) return "";
  const date = new Date(timestamp);
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);
  if (date.toDateString() === today.toDateString()) return "Today";
  if (date.toDateString() === yesterday.toDateString()) return "Yesterday";
  return date.toLocaleDateString([], { month: "short", day: "numeric" });
};

const formatTime = (timestamp) => {
  if (!timestamp) return "";
  return new Date(timestamp).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
};

const ChatListItem = ({ chat, isSelected, onClick }) => {
  const lastMessage = chat.chat_history?.[0];
  const displayName = chat.lead_name || "Web visitor";
  const phoneLine = chat.lead_phone ? chat.lead_phone : null;
  const isGhlDelivery = chat.delivery_mode === "ghl";

  return (
    <button
      onClick={onClick}
      className={`w-full p-4 flex items-start gap-3 transition-all text-left ${
        isSelected
          ? "bg-[#00D1FF]/10 border-l-2 border-[#00D1FF]"
          : "hover:bg-white/5 border-l-2 border-transparent"
      }`}
    >
      <div className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center flex-shrink-0">
        <User className="w-5 h-5 text-[#9CA3AF]" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between mb-1 gap-2">
          <span className="font-body text-sm text-[#F3F4F6] font-medium truncate">{displayName}</span>
          <span className="text-xs text-[#9CA3AF] flex-shrink-0">
            {formatDate(chat.timestamps?.updated_at)}
          </span>
        </div>
        {isGhlDelivery && (
          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium bg-[#01B47A]/10 text-[#01B47A] border border-[#01B47A]/20 mb-1">
            {WEBSITE_LEAD_INTERCEPTOR_COPY.ghlDeliveryBadge(chat.ghl_delivery_channel)}
          </span>
        )}
        {phoneLine && (
          <p className="text-xs text-[#00D1FF]/80 truncate mb-0.5">{phoneLine}</p>
        )}
        <p className="text-xs text-[#9CA3AF] truncate">
          {lastMessage?.content || "No messages yet"}
        </p>
      </div>
    </button>
  );
};

const MessageBubble = ({ message }) => {
  const isUser = message.role === "user";
  return (
    <div className={`flex ${isUser ? "justify-end" : "justify-start"} mb-3`}>
      <div className={`flex items-end gap-2 max-w-[85%] min-w-0 ${isUser ? "flex-row-reverse" : ""}`}>
        <div className="w-7 h-7 rounded-full bg-[#00D1FF]/15 flex items-center justify-center flex-shrink-0">
          {isUser ? (
            <User className="w-3.5 h-3.5 text-[#00D1FF]" />
          ) : (
            <Bot className="w-3.5 h-3.5 text-[#00D1FF]" />
          )}
        </div>
        <div
          className={`min-w-0 rounded-2xl px-4 py-2.5 text-sm font-body leading-relaxed whitespace-pre-wrap break-words [overflow-wrap:anywhere] ${
            isUser
              ? "bg-[#00D1FF]/15 text-[#F3F4F6] rounded-br-md"
              : "bg-white/5 text-[#F3F4F6] rounded-bl-md border border-white/10"
          }`}
        >
          {message.content}
          {message.timestamp && (
            <p className="text-[10px] text-[#9CA3AF] mt-1">{formatTime(message.timestamp)}</p>
          )}
        </div>
      </div>
    </div>
  );
};

const ChatWebWidget = () => {
  const [chats, setChats] = useState([]);
  const [selectedChatId, setSelectedChatId] = useState(null);
  const [chatMessages, setChatMessages] = useState({});
  const [searchQuery, setSearchQuery] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);
  const [error, setError] = useState(null);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const messagesEndRef = useRef(null);

  const fetchChatHistory = useCallback(
    async (chatId) => {
      if (!chatId || chatMessages[chatId]) return;

      const token = getClientAuthToken();
      if (!token) return;

      setIsLoadingHistory(true);
      try {
        const response = await axios.post(
          `${BACKEND_URL}/api/agent/v1/get-test-chat-history`,
          { token, chat_id: chatId },
          { headers: { "Content-Type": "application/json" } },
        );
        if (response.data.status === "ok") {
          setChatMessages((prev) => ({
            ...prev,
            [chatId]: response.data.chat_history,
          }));
        }
      } catch (err) {
        console.error("Error fetching chat history:", err);
      } finally {
        setIsLoadingHistory(false);
      }
    },
    [chatMessages],
  );

  useEffect(() => {
    const fetchChats = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const token = getClientAuthToken();
        if (!token) {
          setError("Not authenticated");
          return;
        }

        const response = await axios.get(`${BACKEND_URL}/api/agent/v1/get_web_widget_chats`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        if (response.data.status === "ok") {
          setChats(response.data.chats);
          if (response.data.chats.length > 0) {
            setSelectedChatId(response.data.chats[0].id);
          }
        }
      } catch (err) {
        console.error("Error fetching web widget chats:", err);
        setError(WEBSITE_LEAD_INTERCEPTOR_COPY.loadChatsError);
      } finally {
        setIsLoading(false);
      }
    };

    fetchChats();
  }, []);

  useEffect(() => {
    if (selectedChatId) {
      fetchChatHistory(selectedChatId);
    }
  }, [selectedChatId, fetchChatHistory]);

  const selectedChat = chats.find((chat) => chat.id === selectedChatId);
  const currentMessages = chatMessages[selectedChatId] || [];

  const filteredChats = chats.filter((chat) => {
    const name = chat.lead_name || "Web visitor";
    const lastMessage = chat.chat_history?.[0]?.content || "";
    const query = searchQuery.toLowerCase();
    return name.toLowerCase().includes(query) || lastMessage.toLowerCase().includes(query);
  });

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [currentMessages]);

  return (
    <div className="min-h-screen bg-[#0F1115]">
      <Sidebar />
      <div className="md:ml-64 min-h-screen">
        <div className="h-screen flex flex-col">
          <header className="bg-[#0F1115]/80 backdrop-blur-lg border-b border-white/5 p-4 md:p-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-[#00D1FF]/10 flex items-center justify-center">
                <Globe className="w-5 h-5 text-[#00D1FF]" />
              </div>
              <div>
                <h1 className="font-heading font-bold text-xl md:text-2xl text-[#F3F4F6]">
                  {WEBSITE_LEAD_INTERCEPTOR_COPY.chatsPageTitle}
                </h1>
                <p className="text-sm text-[#9CA3AF]">{WEBSITE_LEAD_INTERCEPTOR_COPY.chatsSubtitle}</p>
              </div>
            </div>
          </header>

          <div className="flex-1 flex min-w-0 overflow-hidden">
            <div className="w-80 border-r border-white/5 flex-col bg-[#0F1115] hidden lg:flex">
              <div className="p-4 border-b border-white/5">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#9CA3AF]" />
                  <input
                    type="text"
                    placeholder="Search chats..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full bg-white/5 border border-white/10 rounded-lg pl-10 pr-4 py-2.5 text-[#F3F4F6] placeholder-[#9CA3AF]/50 focus:outline-none focus:border-[#00D1FF]/50 text-sm"
                  />
                </div>
              </div>

              <div className="flex-1 overflow-y-auto">
                {isLoading ? (
                  <div className="p-6 text-center">
                    <Loader2 className="w-6 h-6 text-[#00D1FF] animate-spin mx-auto mb-2" />
                    <p className="text-[#9CA3AF] text-sm">Loading chats...</p>
                  </div>
                ) : error ? (
                  <div className="p-6 text-center">
                    <p className="text-red-400 text-sm">{error}</p>
                  </div>
                ) : filteredChats.length > 0 ? (
                  filteredChats.map((chat) => (
                    <ChatListItem
                      key={chat.id}
                      chat={chat}
                      isSelected={chat.id === selectedChatId}
                      onClick={() => setSelectedChatId(chat.id)}
                    />
                  ))
                ) : (
                  <div className="p-6 text-center">
                    <MessageSquare className="w-10 h-10 text-[#9CA3AF]/30 mx-auto mb-3" />
                    <p className="text-[#9CA3AF] text-sm">{WEBSITE_LEAD_INTERCEPTOR_COPY.chatsEmpty}</p>
                  </div>
                )}
              </div>
            </div>

            <div className="flex-1 flex flex-col min-w-0">
              <div className="lg:hidden border-b border-white/5 p-4">
                <div className="relative">
                  <button
                    onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                    className="w-full flex items-center justify-between bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-[#F3F4F6]"
                  >
                    <span>{selectedChat?.lead_name || "Select a chat"}</span>
                    <ChevronDown className={`w-5 h-5 transition-transform ${isDropdownOpen ? "rotate-180" : ""}`} />
                  </button>
                  {isDropdownOpen && (
                    <div className="absolute top-full left-0 right-0 mt-2 bg-[#1a1d21] border border-white/10 rounded-lg shadow-xl z-50 max-h-64 overflow-y-auto">
                      {chats.map((chat) => (
                        <button
                          key={chat.id}
                          onClick={() => {
                            setSelectedChatId(chat.id);
                            setIsDropdownOpen(false);
                          }}
                          className="w-full p-3 text-left hover:bg-white/5 text-sm text-[#F3F4F6]"
                        >
                          {chat.lead_name || "Web visitor"}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {selectedChat ? (
                <>
                  <div className="hidden lg:flex items-center px-6 py-4 border-b border-white/5">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center">
                        <User className="w-5 h-5 text-[#9CA3AF]" />
                      </div>
                      <div>
                        <h2 className="font-heading font-medium text-[#F3F4F6]">
                          {selectedChat.lead_name || "Web visitor"}
                        </h2>
                        {selectedChat.lead_phone ? (
                          <p className="text-xs text-[#00D1FF]/80">{selectedChat.lead_phone}</p>
                        ) : null}
                        <p className="text-xs text-[#9CA3AF]">
                          Session {selectedChat.lead_contact?.slice(0, 8)}...
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="flex-1 min-w-0 overflow-y-auto p-4 md:p-6">
                    {isLoadingHistory && currentMessages.length === 0 ? (
                      <div className="flex items-center justify-center h-full">
                        <Loader2 className="w-6 h-6 text-[#00D1FF] animate-spin" />
                      </div>
                    ) : (
                      <>
                        {currentMessages.map((message, index) => (
                          <MessageBubble key={`${message.timestamp}-${index}`} message={message} />
                        ))}
                        <div ref={messagesEndRef} />
                      </>
                    )}
                  </div>
                </>
              ) : (
                <div className="flex-1 flex items-center justify-center text-[#9CA3AF] text-sm">
                  Select a chat to view the conversation
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChatWebWidget;
